// src/pages/SubHeadquarter/ViewSubHeadquarters.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subHeadquarterService from '../../services/subHeadquarter.service';
import '../../styles/ViewPages.css';

const ViewSubHeadquarters = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [subHeadquarters, setSubHeadquarters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubHeadquarters();
  }, [currentPage, searchTerm]);

  const fetchSubHeadquarters = async () => {
    setLoading(true);
    try {
      const companyId = isSuperAdmin 
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      if (!companyId) {
        alert('Please select a company first');
        setLoading(false);
        return;
      }

      const response = await subHeadquarterService.getSubHeadquarters(
        companyId,
        currentPage,
        10,
        searchTerm
      );

      if (response.success && response.data) {
        setSubHeadquarters(response.data.subHeadquarters || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching sub-headquarters:', error);
      alert('Failed to fetch sub-headquarters');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const response = await subHeadquarterService.deleteSubHeadquarter(id);
        if (response.success) {
          alert('Sub-Headquarter deleted successfully!');
          fetchSubHeadquarters();
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete sub-headquarter');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="view-subheadquarters-container">
      {/* Header */}
      <div className="view-header">
        <h1 className="view-title">View Sub Headquarters</h1>
        <div className="view-actions">
          <button
            className="btn-add"
            onClick={() => navigate('/subhq/add')}
          >
            + Add Sub Headquarter
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search sub headquarters by company... 🔍"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading sub-headquarters...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>COMPANY</th>
                <th>REGION</th>
                <th>HEADQUARTER</th>
                <th>SUB HEADQUARTER</th>
                <th>ADDED DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {subHeadquarters.length > 0 ? (
                subHeadquarters.map((subHq) => (
                  <tr key={subHq.id}>
                    <td>{subHq.company?.name || 'N/A'}</td>
                    <td>{subHq.region?.name || 'N/A'}</td>
                    <td>{subHq.headquarter?.name || 'N/A'}</td>
                    <td>{subHq.name}</td>
                    <td>{formatDate(subHq.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/subhq/view/${subHq.id}`)}
                          title="View Details"
                        >
                          view
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => navigate(`/subhq/edit/${subHq.id}`)}
                          title="Edit Sub-Headquarter"
                        >
                          edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(subHq.id, subHq.name)}
                          title="Delete Sub-Headquarter"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">
                    {searchTerm ? 'No sub-headquarters found matching your search' : 'No sub-headquarters available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            « Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next »
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewSubHeadquarters;